import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { TIKTOK } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_PERCENTAGE,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP,
  ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD,
  IMPRESSIONS,
  RESULTS,
  CTR,
  CLICKS,
  AGE_RANGE_PLACEHOLDER,
  GENDERS_PLACEHOLDER,
  MALES,
  FEMALES,
  LOCATIONS_PLACEHOLDER,
  INTERESTS_PLACEHOLDER,
  BEHAVIORS_PLACEHOLDER,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import PerformingAssetsCard from "../components/PerformingAssetsCard";
import PerformingAssetsBento, {
  PerformingAssetsBentoItem,
} from "../components/PerformingAssetsBento";
import PerformingAssetsSegmentation from "../components/PerformingAssetsSegmentation";
import PerformingAssetsCreative from "../components/PerformingAssetsCreative";
import { buildPerformingAssetSummary } from "../components/buildPerformingAssetSummary";
import {
  canShowPerformingEntity,
  findCampaignEntityByID,
} from "../components/performingAssetsMetrics";
import { openLink } from "../../../../../utils/string";

const TiktokPerformingAssets = () => {
  const { t } = useTranslationApp();
  const { campaign, campaignID } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);

  // Returns the best ad group, prioritizing mainAction, otherwise by click
  const getBestAdGroup = (adGroupsStatistics) => {
    if (!adGroupsStatistics) return null;

    let bestByMainAction = null;
    let bestByClick = null;
    let hasMainAction = false;

    Object.entries(adGroupsStatistics).forEach(([adGroupId, stats]) => {
      if (!stats) return;
      if (stats.mainAction && stats.mainAction > 0) {
        hasMainAction = true;
        if (
          !bestByMainAction ||
          stats.mainAction > bestByMainAction.statistics.mainAction
        ) {
          bestByMainAction = {
            adGroupId,
            statistics: stats,
          };
        }
      }
      if (
        !bestByClick ||
        (stats.click || 0) > (bestByClick.statistics.click || 0)
      ) {
        bestByClick = {
          adGroupId,
          statistics: stats,
        };
      }
    });

    return hasMainAction ? bestByMainAction : bestByClick;
  };

  // Returns the best ad, prioritizing mainAction, otherwise by click
  const getBestAd = (adsGroups, statistics) => {
    if (!Array.isArray(adsGroups) || !statistics) return null;

    let bestAdByMainAction = null;
    let bestAdByClicks = null;
    let hasMainAction = false;

    adsGroups.forEach((adGroup) => {
      if (adGroup?.platform !== TIKTOK || !Array.isArray(adGroup.ads)) return;

      adGroup.ads.forEach((ad) => {
        if (!ad?.id) return;
        const adStats = statistics[ad.id];
        if (!adStats) return;

        if (adStats.mainAction && adStats.mainAction > 0) {
          hasMainAction = true;
          if (
            !bestAdByMainAction ||
            adStats.mainAction > bestAdByMainAction.statistics.mainAction
          ) {
            bestAdByMainAction = {
              adGroupId: adGroup.id,
              adId: ad.id,
              statistics: adStats,
            };
          }
        }

        if (
          !bestAdByClicks ||
          (adStats.click || 0) > (bestAdByClicks.statistics.click || 0)
        ) {
          bestAdByClicks = {
            adGroupId: adGroup.id,
            adId: ad.id,
            statistics: adStats,
          };
        }
      });
    });

    return hasMainAction ? bestAdByMainAction : bestAdByClicks;
  };

  const tiktokStats = statistics?.[TIKTOK] ?? {};
  const bestAdGroup = getBestAdGroup(tiktokStats.adsGroups);
  const bestAd = getBestAd(campaign?.adsGroups, tiktokStats.ads);

  const getAdGroupByID = (id) => {
    const adGroup = findCampaignEntityByID({
      entities: campaign?.adsGroups,
      id,
      platform: TIKTOK,
    });
    if (!adGroup?.id) return {};
    return { adGroup, adGroupID: adGroup.id };
  };

  const getAdByID = (adId) => {
    if (!adId) {
      return {};
    }
    const adGroupsTikTok = (campaign?.adsGroups ?? []).filter(
      (adGroup) => adGroup.platform === TIKTOK
    );

    const foundAdGroup = adGroupsTikTok.find(
      (adGroup) =>
        Array.isArray(adGroup?.ads) && adGroup.ads.some((ad) => ad.id === adId)
    );

    if (!foundAdGroup) {
      return {};
    }

    const ad = foundAdGroup.ads.find((item) => item.id === adId);
    if (!ad?.id) return {};
    const adGroupIndex = adGroupsTikTok.findIndex(
      (group) => group.id === foundAdGroup.id
    );
    const adGroupID = foundAdGroup.id;

    return {
      ad,
      adID: ad.id,
      adGroupIndex,
      adGroupID,
    };
  };

  const { adGroup = {}, adGroupID } = getAdGroupByID(bestAdGroup?.adGroupId);
  const { ad = {}, adID, adGroupID: adGroupIDAd } = getAdByID(bestAd?.adId);

  const summaryLabels = {
    t,
    impressionsLabel: t(IMPRESSIONS),
    ctrLabel: t(CTR),
    clicksLabel: t(CLICKS),
    resultsLabel: t(RESULTS),
    valueNumber: VALUE_NUMBER,
    valuePercentage: VALUE_WITH_PERCENTAGE,
  };

  const showBestAdGroup = canShowPerformingEntity({
    entity: adGroup,
    statistics: bestAdGroup?.statistics,
  });
  const showBestAd = canShowPerformingEntity({
    entity: ad,
    statistics: bestAd?.statistics,
  });

  const bestAdGroupStats = showBestAdGroup
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAdGroup.statistics,
      })
    : [];

  const bestAdStats = showBestAd
    ? buildPerformingAssetSummary({
        ...summaryLabels,
        statistics: bestAd.statistics,
      })
    : [];

  const creativeUrl = ad?.mediaUrls?.[0];
  const sections = [
    showBestAdGroup ? (
      <PerformingAssetsBentoItem key="bestAdGroup" span={8}>
        <PerformingAssetsCard
          platform={TIKTOK}
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD_GROUP)}
          subtitle={adGroup.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${TIKTOK}&adGroupID=${adGroupID}`
            )
          }
          statistics={bestAdGroupStats}
          nextContent={<BestAdGroupSegmentation adGroup={adGroup} />}
        />
      </PerformingAssetsBentoItem>
    ) : null,
    showBestAd ? (
      <PerformingAssetsBentoItem key="bestAd" span={4}>
        <PerformingAssetsCard
          platform={TIKTOK}
          layout="creative"
          title={t(ADS_CAMPAIGNS_STATISTICS_INSIGHTS_BEST_PERFORMING_AD)}
          subtitle={ad.name}
          onSubtitleClick={() =>
            openLink(
              `ads-campaign?campaignID=${campaignID}&platform=${TIKTOK}&adGroupID=${adGroupIDAd}&adID=${adID}`
            )
          }
          statistics={bestAdStats}
          media={
            creativeUrl ? <PerformingAssetsCreative url={creativeUrl} /> : null
          }
        />
      </PerformingAssetsBentoItem>
    ) : null,
  ].filter(Boolean);

  if (!sections.length) return null;

  return <PerformingAssetsBento>{sections}</PerformingAssetsBento>;
};

const SEGMENTATION_FIELDS = [
  "ageRange",
  "genders",
  "locations",
  "interests",
  "behaviors",
];
const BestAdGroupSegmentation = ({ adGroup = {} }) => {
  const { t } = useTranslationApp();

  const getSegmentationFields = () => {
    const segmentationFieldsData = [];

    SEGMENTATION_FIELDS.forEach((field) => {
      const fieldData = adGroup[field];
      if (!fieldData || fieldData?.length === 0) return;
      if (field === "ageRange") {
        const [minAge, maxAge] = fieldData || [];
        segmentationFieldsData.push({
          label: t(AGE_RANGE_PLACEHOLDER),
          value: `${minAge}-${maxAge}`,
        });
      } else if (field === "genders") {
        const genders = fieldData || {};
        segmentationFieldsData.push({
          label: t(GENDERS_PLACEHOLDER),
          value: Object.keys(genders)
            .filter((gender) => genders[gender] === true)
            .map((gender) => t(gender === "male" ? MALES : FEMALES))
            .join(", "),
        });
      } else {
        const value = fieldData.map((item) => item.name).join(", ");
        let label = "";
        switch (field) {
          case "locations":
            label = t(LOCATIONS_PLACEHOLDER);
            break;
          case "interests":
            label = t(INTERESTS_PLACEHOLDER);
            break;
          case "behaviors":
            label = t(BEHAVIORS_PLACEHOLDER);
            break;
        }

        segmentationFieldsData.push({
          label,
          value,
        });
      }
    });

    return segmentationFieldsData;
  };

  const segmentationFields = getSegmentationFields();

  return <PerformingAssetsSegmentation fields={segmentationFields} />;
};

export default TiktokPerformingAssets;
